import React, { type PropsWithChildren, useMemo } from 'react';
import type {
  ContentLexicalStateV1,
  SupportedLexicalDisplayNode,
} from '@soundxyz/utils/lexical/ContentLexicalStateV1';
import { LexicalDisplayNodesMention } from '@components/Lexical/Display/Nodes/Mention';

export function LexicalDisplayNodes({
  nodes,
  depth,
  showMentionsHoverCard,
}: PropsWithChildren<{
  depth: number;
  nodes: Array<SupportedLexicalDisplayNode | ContentLexicalStateV1['root']>;
  showMentionsHoverCard: boolean;
}>) {
  const content = useMemo(() => {
    return nodes.map((node, index) => {
      switch (node.type) {
        case 'text':
          return (
            <span data-feed-clickable-div key={`text-${depth}-${index}`}>
              {node.text}
            </span>
          );
        case 'paragraph':
          if (node.children.length) {
            return (
              <p data-feed-clickable-div key={`p-${depth}-${index}`}>
                <LexicalDisplayNodes
                  showMentionsHoverCard={showMentionsHoverCard}
                  nodes={node.children}
                  depth={depth + 1}
                />
              </p>
            );
          } else {
            return null;
          }
        case 'root':
          return (
            <LexicalDisplayNodes
              showMentionsHoverCard={showMentionsHoverCard}
              key={`root-${depth}-${index}`}
              nodes={node.children}
              depth={depth + 1}
            />
          );

        case 'linebreak':
          return (
            <span key={`linebreak-${depth}-${index}`}>
              <br key={`br-${depth}-${index}`} />
              <span key={`zeroWidth-${depth}-${index}`}>&#8203;</span>
            </span>
          );
        case 'zeroWidth':
          return <span key={`zeroWidth-${depth}-${index}`}>&#8203;</span>;
        case 'custom-beautifulMention':
          return (
            <LexicalDisplayNodesMention
              showMentionsHoverCard={showMentionsHoverCard}
              webappUri={node.data.webappUri}
              key={`custom-beautifulMention-${depth}-${index}`}
              displayText={node.data.displayName}
              userId={node.data.userId}
            />
          );

        default:
          return null;
      }
    });
  }, [nodes, depth, showMentionsHoverCard]);

  return content;
}
